import { useState } from 'react'
import dayjs from '@dcp/shared/utils/dayjs'
import {
  KpiCards,
  CrossCheckingFilters,
  TransactionMatchingTabs,
  CrossCheckingTable,
} from '@/components/banking'
import {
  useCrossCheckingKpi,
  useCrossCheckingTransactions,
  useFacilities,
} from '@/hooks/useBanking'
import { tableQueryState } from '@/components/common/queryErrorDisplay'
import type { BankingFilters, CrossCheckingTab, CrossCheckingTransaction } from '@/types/banking'

const EMPTY_TRANSACTIONS: CrossCheckingTransaction[] = []

/**
 * Cross-checking page for banking reconciliation
 *
 * Displays:
 * - The shared KPI summary cards (bank balance, outstanding checks, book balance, etc.)
 * - Date and facility filters
 * - Tab navigation (Matching, Intact Balance Report, History)
 * - Transaction data table with 12 columns
 */
export function CrossCheckingPage() {
  // State management
  const [filters, setFilters] = useState<BankingFilters>({
    endDate: dayjs().format('YYYY-MM-DD'), // Today's date in YYYY-MM-DD format
  })
  const [activeTab, setActiveTab] = useState<CrossCheckingTab>('matching')

  // Data fetching
  const { data: kpi, isLoading: kpiLoading } = useCrossCheckingKpi(filters)
  const transactionsQuery = useCrossCheckingTransactions(filters, activeTab)
  const transactions = transactionsQuery.data ?? EMPTY_TRANSACTIONS
  const { data: facilities = [] } = useFacilities()

  return (
    <div className="space-y-6">
      {/* Filters */}
      <CrossCheckingFilters
        filters={filters}
        onFiltersChange={setFilters}
        facilities={facilities}
      />

      {/* Aggregated balances (DCP-297): the same KpiCards row as every other
          Banking tab, rendered unconditionally so the page does not jump when
          the KPI query resolves. */}
      <KpiCards kpi={kpi} loading={kpiLoading} />

      {/* Transaction Matching Tabs */}
      <TransactionMatchingTabs activeTab={activeTab} onTabChange={setActiveTab}>
        {{
          matching: (
            <CrossCheckingTable
              transactions={transactions}
              {...tableQueryState(transactionsQuery, transactions.length)}
            />
          ),
          intactBalanceReport: (
            <div>
              <h3 className="mb-4 text-lg font-semibold">Intact Balance Report</h3>
              <div className="rounded-md border p-8 text-center text-muted-foreground">
                Intact Balance Report coming soon
              </div>
            </div>
          ),
          history: (
            <div>
              <h3 className="mb-4 text-lg font-semibold">
                Matching and Cross-checking History
              </h3>
              <div className="rounded-md border p-8 text-center text-muted-foreground">
                History view coming soon
              </div>
            </div>
          ),
        }}
      </TransactionMatchingTabs>
    </div>
  )
}